import api from './api';
import { TeamMember, CreateTeamMemberData, UpdateTeamMemberData } from '../types/team.types';
import { ApiResponse, PaginatedResponse } from '../types/common.types';

interface GetTeamMembersParams {
  page?: number;
  limit?: number;
  role?: string;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

class TeamService {
  /**
   * Get all team members with filters
   * Route: GET /api/auth/users
   */
  async getTeamMembers(params: GetTeamMembersParams = {}): Promise<PaginatedResponse<{ users: TeamMember[] }>> {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.role) queryParams.append('role', params.role);
      if (params.search) queryParams.append('search', params.search);
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.order) queryParams.append('order', params.order);

      console.log('🔍 Fetching team members with params:', Object.fromEntries(queryParams));

      const response = await api.get<PaginatedResponse<{ users: TeamMember[] }>>(
        `/admin/users?${queryParams.toString()}`
      );

      console.log(`✅ Fetched ${response.data.data?.users?.length || 0} team members`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch team members:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Get single team member by ID
   * Route: GET /api/auth/users/:userId (use current user endpoint for now)
   */
  async getTeamMemberById(id: string): Promise<TeamMember> {
    try {
      console.log('🔍 Fetching team member with ID:', id);

      // Note: Backend doesn't have individual user endpoint, so we fetch all and filter
      const response = await api.get<PaginatedResponse<{ users: TeamMember[] }>>('/admin/users');
      const user = response.data.data?.users.find(u => u.id === id);

      if (!user) {
        throw new Error('User not found');
      }

      console.log('✅ Team member fetched successfully:', user.name);
      return user;
    } catch (error: any) {
      console.error('❌ Failed to fetch team member:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Create new team member
   * Route: POST /api/auth/create-user
   */
  async createTeamMember(data: CreateTeamMemberData): Promise<TeamMember> {
    try {
      console.log('📝 Creating team member:', data.email);

      const formData = new FormData();

      // Required fields
      formData.append('name', data.name.trim());
      formData.append('email', data.email.trim().toLowerCase());
      formData.append('userID', data.userID.trim().toUpperCase());
      formData.append('mobile', data.mobile.trim());
      formData.append('password', data.password);
      formData.append('role', data.role);

      // Profile photo
      if (data.profilePhoto) {
        formData.append('profilePhoto', data.profilePhoto);
        console.log('📷 Profile photo attached');
      }

      // Log form data
      console.log('📤 Sending form data:');
      

      const response = await api.post<ApiResponse<{ user: TeamMember }>>(
        '/admin/create-user',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('✅ Team member created successfully:', response.data.data?.user?.email);
      return response.data.data!.user;
    } catch (error: any) {
      console.error('❌ Failed to create team member:', error.response?.data || error.message);
      
      if (error.response?.data?.errors) {
        console.error('Validation errors:', error.response.data.errors);
      }
      
      throw error;
    }
  }

  /**
   * Update team member by ID
   * Route: PUT /api/auth/users/:userId
   */
  async updateTeamMember(id: string, data: UpdateTeamMemberData): Promise<TeamMember> {
    try {
      console.log('💾 Updating team member with ID:', id);

      const formData = new FormData();

      // Add only provided fields
      if (data.name) formData.append('name', data.name.trim());
      if (data.email) formData.append('email', data.email.trim().toLowerCase());
      if (data.mobile) formData.append('mobile', data.mobile.trim());
      if (data.role) formData.append('role', data.role);
      
      if (data.isActive !== undefined) {
        formData.append('isActive', String(data.isActive));
      }

      // Profile photo
      if (data.profilePhoto) {
        formData.append('profilePhoto', data.profilePhoto);
        console.log('   📷 New profile photo attached');
      }

      const response = await api.put<ApiResponse<{ user: TeamMember }>>(
        `/admin/users/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('✅ Team member updated successfully:', response.data.data?.user?.email);
      return response.data.data!.user;
    } catch (error: any) {
      console.error('❌ Failed to update team member:', error.response?.data || error.message);
      
      if (error.response?.data?.errors) {
        console.error('Validation errors:', error.response.data.errors);
      }
      
      throw error;
    }
  }

  /**
   * Delete team member by ID
   * Route: DELETE /api/auth/users/:userId
   */
  async deleteTeamMember(id: string): Promise<void> {
    try {
      console.log('🗑️ Deleting team member with ID:', id);

      await api.delete(`/admin/users/${id}`);

      console.log('✅ Team member deleted successfully');
    } catch (error: any) {
      console.error('❌ Failed to delete team member:', error.response?.data || error.message);
      throw error;
    }
  }
}

export default new TeamService();